import React, {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiX, FiPhone, FiMail, FiClock, FiMapPin} = FiIcons;

const ContactSidebar = ({isOpen, onClose}) => {
  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Sidebar - Erweitert auf sm:w-[480px] und lg:w-[520px] */}
          <motion.div
            initial={{x: '100%'}}
            animate={{x: 0}}
            exit={{x: '100%'}}
            transition={{type: 'spring', damping: 25, stiffness: 200}}
            className="fixed right-0 top-0 h-full w-full sm:w-[480px] lg:w-[520px] bg-white shadow-2xl z-[9999] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Kontakt</h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
              <p className="text-white/90">
                Wir freuen uns auf Ihre Nachricht
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* RUDT Bau Logo */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.1}}
                className="relative"
              >
                <div className="aspect-square w-full rounded-xl overflow-hidden shadow-lg mb-4 bg-white p-8 flex items-center justify-center">
                  <img
                    src="https://canv.wasacon.com/rudt-bau/_assets/media/be98d041c9972c824096f49dc08c4e70.png"
                    alt="RUDT Bau Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Waldemar Rudt
                  </h3>
                  <p style={{color: 'rgb(52, 102, 173)'}} className="text-[#3466ad] font-medium">
                    Geschäftsführer & Maurermeister
                  </p>
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.2}}
                className="space-y-4"
              >
                {/* Phone */}
                <a
                  href="tel:+4924528607019"
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#3466ad] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#2a5291] transition-colors">
                    <SafeIcon icon={FiPhone} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Telefon</p>
                    <p className="text-lg font-semibold text-gray-900">
                      02452 860 70 19
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href="mailto:info@rudtbau.de"
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#3466ad] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#2a5291] transition-colors">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">E-Mail</p>
                    <p className="text-lg font-semibold text-gray-900 break-all">
                      info@rudtbau.de
                    </p>
                  </div>
                </a>

                {/* Opening Hours */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-[#3466ad] rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiClock} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">Öffnungszeiten</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700 font-medium">Mo - Fr:</span>
                        <span className="text-gray-900 font-semibold">7:00 - 17:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700 font-medium">Sa:</span>
                        <span className="text-gray-900 font-semibold">8:00 - 12:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700 font-medium">So:</span>
                        <span className="text-gray-900 font-semibold">Geschlossen</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-[#3466ad] rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Adresse</p>
                    <p className="text-gray-900 font-semibold">
                      Wassenbergerstr. 41<br />
                      52525 Heinsberg
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3}}
              >
                <a
                  href="tel:+4924528607019"
                  className="block w-full bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white text-center py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-200"
                >
                  Jetzt anrufen
                </a>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactSidebar;